// Small floating card that pops up near the notification bell the instant a
// new notification arrives over the socket, showing who it's from. Unlike the
// generic toast() banners (utils/toast.js), this stays on screen until the
// user dismisses it — it's a persistent "you have something new" flag, not a
// timed message.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, UserPlus, Mail, Award, Star, Users, HeartHandshake, Sparkles, FileText, ClipboardCheck, Bell, X } from 'lucide-react';
import { useSocket } from './Context.jsx';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';

const API = `${GREENWICH_BACKEND_URL}/api`;
const DEFAULT_AVATAR = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';

const buildAvatarUrl = (src) => {
  if (!src || src.trim() === '') return DEFAULT_AVATAR;
  if (src.startsWith('http')) return src;
  return `${GREENWICH_BACKEND_URL}${src.startsWith('/') ? src : '/' + src}`;
};

const TYPE_META = {
  like:            { cls: 'ntoast-heart',   icon: <Heart size={11} color="#fff" /> },
  post_like:       { cls: 'ntoast-heart',   icon: <Heart size={11} color="#fff" /> },
  comment:         { cls: 'ntoast-comment', icon: <MessageCircle size={11} color="#fff" /> },
  post_comment:    { cls: 'ntoast-comment', icon: <MessageCircle size={11} color="#fff" /> },
  mention:         { cls: 'ntoast-comment', icon: <MessageCircle size={11} color="#fff" /> },
  follow:          { cls: 'ntoast-follow',  icon: <UserPlus size={11} color="#fff" /> },
  follow_request:  { cls: 'ntoast-follow',  icon: <UserPlus size={11} color="#fff" /> },
  follow_accepted: { cls: 'ntoast-follow',  icon: <UserPlus size={11} color="#fff" /> },
  message:         { cls: 'ntoast-message', icon: <Mail size={11} color="#fff" /> },
  new_message:     { cls: 'ntoast-message', icon: <Mail size={11} color="#fff" /> },
  certificate_issued:     { cls: 'ntoast-follow', icon: <Award size={11} color="#fff" /> },
  org_review_received:    { cls: 'ntoast-follow', icon: <Star size={11} color="#fff" /> },
  join_request:            { cls: 'ntoast-follow', icon: <Users size={11} color="#fff" /> },
  join_request_response:   { cls: 'ntoast-follow', icon: <Users size={11} color="#fff" /> },
  application_submitted:   { cls: 'ntoast-follow', icon: <FileText size={11} color="#fff" /> },
  application_response:    { cls: 'ntoast-follow', icon: <FileText size={11} color="#fff" /> },
  application_attended:    { cls: 'ntoast-follow', icon: <ClipboardCheck size={11} color="#fff" /> },
  level_up:                { cls: 'ntoast-follow', icon: <Sparkles size={11} color="#fff" /> },
  buddy_mentor_application: { cls: 'ntoast-follow', icon: <HeartHandshake size={11} color="#fff" /> },
  buddy_mentor_approved:    { cls: 'ntoast-follow', icon: <HeartHandshake size={11} color="#fff" /> },
  buddy_request_accepted:   { cls: 'ntoast-follow', icon: <HeartHandshake size={11} color="#fff" /> },
};

const styles = `
  .ntoast-stack { position: fixed; top: 78px; right: 18px; z-index: 1450; display: flex; flex-direction: column; gap: 10px; pointer-events: none; }
  @media (max-width: 768px) { .ntoast-stack { top: 64px; right: 10px; left: 10px; } }
  .ntoast-card { pointer-events: all; width: min(320px, 100%); display: flex; align-items: flex-start; gap: 10px; padding: 12px 12px; background: #f5f7f0; border: 1px solid rgba(50,97,77,0.28); border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,0.16), 0 0 24px rgba(50,97,77,0.1); cursor: pointer; font-family: 'Sora', sans-serif; animation: ntoastIn 0.34s cubic-bezier(0.34,1.56,0.64,1); }
  .ntoast-card.leaving { animation: ntoastOut 0.22s ease forwards; }
  @keyframes ntoastIn { from { opacity: 0; transform: translateX(24px) scale(0.94); } to { opacity: 1; transform: translateX(0) scale(1); } }
  @keyframes ntoastOut { from { opacity: 1; transform: translateX(0) scale(1); } to { opacity: 0; transform: translateX(24px) scale(0.94); } }
  .ntoast-avatar-wrap { position: relative; flex-shrink: 0; }
  .ntoast-avatar-ring { position: absolute; inset: -4px; border-radius: 50%; border: 2px solid #faa002; opacity: 0.9; animation: ntoastPing 1.4s cubic-bezier(0,0,0.2,1) infinite; }
  @keyframes ntoastPing { 0% { transform: scale(0.9); opacity: 0.8; } 75%, 100% { transform: scale(1.5); opacity: 0; } }
  .ntoast-avatar-img { width: 38px; height: 38px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(50,97,77,0.25); display: block; position: relative; z-index: 1; }
  .ntoast-type-badge { position: absolute; bottom: -3px; right: -3px; width: 17px; height: 17px; border-radius: 6px; display: flex; align-items: center; justify-content: center; border: 2px solid #f5f7f0; z-index: 2; }
  .ntoast-heart   { background: linear-gradient(135deg,#f43f5e,#e11d48); }
  .ntoast-comment { background: linear-gradient(135deg,#faa002,#e08e00); }
  .ntoast-follow  { background: linear-gradient(135deg,#32614d,#254a3a); }
  .ntoast-message { background: linear-gradient(135deg,#faa002,#e08e00); }
  .ntoast-body { flex: 1; min-width: 0; }
  .ntoast-sender { font-size: 12.5px; font-weight: 700; color: #32614d; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ntoast-text { font-size: 12px; color: #5a6e65; line-height: 1.4; margin-top: 1px; }
  .ntoast-close { flex-shrink: 0; width: 24px; height: 24px; border-radius: 7px; background: transparent; border: none; color: #9aaa9f; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.18s; }
  .ntoast-close:hover { background: rgba(239,68,68,0.1); color: #ef4444; }
`;

const NotificationToast = () => {
  const { on, off } = useSocket();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const leavingRef = useRef(new Set());

  const getActionText = useCallback((notif) => {
    if (notif.message) return notif.message;
    const map = {
      like: t('notifications.liked'),
      post_like: t('notifications.liked'),
      comment: t('notifications.commented'),
      post_comment: t('notifications.commented'),
      follow: t('notifications.followed'),
      follow_request: t('notifications.followRequest'),
      follow_accepted: t('notifications.followAccepted'),
      message: t('notifications.sentMessage'),
      new_message: t('notifications.sentMessage'),
    };
    return map[notif.type] || t('notifications.genericNotification');
  }, [t]);

  const dismiss = useCallback((id, alsoDelete) => {
    leavingRef.current.add(id);
    setItems(prev => [...prev]); // trigger re-render so the 'leaving' class applies
    setTimeout(() => {
      setItems(prev => prev.filter(i => i.id !== id));
      leavingRef.current.delete(id);
    }, 220);

    if (alsoDelete) {
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${API}/notifications/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    }
  }, []);

  useEffect(() => {
    const handleNew = (notification) => {
      const id = notification._id || notification.id || `${Date.now()}`;
      setItems(prev => [{ id, notification }, ...prev].slice(0, 4));
    };
    const cleanup = on('new-notification', handleNew);
    return () => { off('new-notification', handleNew); cleanup?.(); };
  }, [on, off]);

  const handleCardClick = (item) => {
    const notif = item.notification;
    const senderId = notif.sender?._id || notif.sender?.id;
    dismiss(item.id, false);
    if (senderId && ['follow', 'follow_accepted', 'like', 'post_like', 'comment', 'post_comment', 'mention'].includes(notif.type)) {
      navigate(`/profile/${senderId}`);
    } else if (['message', 'new_message'].includes(notif.type)) {
      navigate('/messages');
    } else {
      navigate('/notifications');
    }
  };

  if (!items.length) return null;

  return (
    <>
      <style>{styles}</style>
      <div className="ntoast-stack">
        {items.map(item => {
          const notif = item.notification;
          const senderName = notif.sender
            ? (notif.sender.organizationName || notif.sender.name || notif.sender.username || t('notifications.someone'))
            : t('notifications.someone');
          const meta = TYPE_META[notif.type] || { cls: 'ntoast-follow', icon: <Bell size={11} color="#fff" /> };
          return (
            <div
              key={item.id}
              className={`ntoast-card${leavingRef.current.has(item.id) ? ' leaving' : ''}`}
              onClick={() => handleCardClick(item)}
            >
              <div className="ntoast-avatar-wrap">
                <div className="ntoast-avatar-ring" />
                <img
                  className="ntoast-avatar-img"
                  src={buildAvatarUrl(notif.sender?.profileImage || notif.sender?.avatar)}
                  alt={senderName}
                  onError={(e) => { e.currentTarget.src = DEFAULT_AVATAR; }}
                />
                <div className={`ntoast-type-badge ${meta.cls}`}>{meta.icon}</div>
              </div>
              <div className="ntoast-body">
                <div className="ntoast-sender">{senderName}</div>
                <div className="ntoast-text">{getActionText(notif)}</div>
              </div>
              <button
                className="ntoast-close"
                title={t('notifications.dismiss')}
                onClick={(e) => { e.stopPropagation(); dismiss(item.id, true); }}
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default NotificationToast;
